// src/pages/admin/CardsAndApplications.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Button,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Search, AccountBalanceWallet } from "@mui/icons-material";
import { motion } from "framer-motion";

// API
import {
  fetchCards,
  blockCard,
  unblockCard,
  adjustCardBalance,
  fetchCardApplications,
  approveCardApplication,
  rejectCardApplication,
} from "../utils/api";

export default function CardsAndApplications() {
  const [tab, setTab] = useState(0);

  // shared pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // cards
  const [cards, setCards] = useState([]);
  const [cardsTotal, setCardsTotal] = useState(0);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [search, setSearch] = useState("");

  // applications
  const [apps, setApps] = useState([]);
  const [appsTotal, setAppsTotal] = useState(0);
  const [appsLoading, setAppsLoading] = useState(true);

  // balance modal
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [newBalance, setNewBalance] = useState("");

  // load cards
  const loadCards = async () => {
    try {
      setCardsLoading(true);
      const data = await fetchCards({
        page: page + 1,
        per_page: rowsPerPage,
        search,
      });
      setCards(data.data);
      setCardsTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setCardsLoading(false);
    }
  };

  // load applications
  const loadApplications = async () => {
    try {
      setAppsLoading(true);
      const data = await fetchCardApplications({
        page: page + 1,
        per_page: rowsPerPage,
      });
      setApps(data.data);
      setAppsTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setAppsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) loadApplications();
    if (tab === 1) loadCards();
  }, [tab, page, rowsPerPage, search]);

  // card actions
  const openBalanceModal = (card) => {
    setSelectedCard(card);
    setNewBalance(card.balance);
    setBalanceModalOpen(true);
  };

  const saveBalance = async () => {
    try {
      await adjustCardBalance(selectedCard.id, {
        amount: Number(newBalance),
        action: Number(newBalance) > selectedCard.balance ? "credit" : "debit",
      });
      await loadCards();
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Card balance updated", severity: "success" },
        })
      );
    } catch {
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Failed to update balance", severity: "error" },
        })
      );
    } finally {
      setBalanceModalOpen(false);
    }
  };

  const toggleBlock = async (card) => {
    try {
      if (card.status === "blocked") {
        await unblockCard(card.id);
      } else {
        await blockCard(card.id);
      }
      await loadCards();
    } catch (err) {
      console.error(err);
    }
  };

  // application actions
  const handleApprove = async (id) => {
    await approveCardApplication(id);
    loadApplications();
  };

  const handleReject = async (id) => {
    await rejectCardApplication(id);
    loadApplications();
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ color: "#0ff", fontWeight: "bold", mb: 2 }}>
        Cards & Applications
      </Typography>

      <Tabs value={tab} onChange={(e, v) => { setTab(v); setPage(0); }}>
        <Tab label="Applications" />
        <Tab label="Issued Cards" />
      </Tabs>

      {/* Applications */}
      {tab === 0 && (
        <Card sx={{ mt: 2, p: 2, bgcolor: "rgba(25,25,50,0.7)" }}>
          <TableContainer>
            {appsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apps.map((app, i) => (
                    <motion.tr key={app.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <TableCell>{app.user?.name}</TableCell>
                      <TableCell>{app.card_brand}</TableCell>
                      <TableCell>{app.card_type}</TableCell>
                      <TableCell>
                        <Chip
                          label={app.status}
                          color={
                            app.status === "pending"
                              ? "warning"
                              : app.status === "approved"
                              ? "success"
                              : "error"
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        {app.status === "pending" && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => handleApprove(app.id)}
                              sx={{ mr: 1 }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => handleReject(app.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <TablePagination
            component="div"
            count={appsTotal}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
          />
        </Card>
      )}

      {/* Issued Cards */}
      {tab === 1 && (
        <Card sx={{ mt: 2, p: 2, bgcolor: "rgba(25,25,50,0.7)" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search cards..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#aaa" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: 2 }}
            />
          </Box>
          <TableContainer>
            {cardsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Card Number</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cards.map((card, i) => (
                    <motion.tr key={card.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <TableCell>{card.card_number}</TableCell>
                      <TableCell>{card.user?.name}</TableCell>
                      <TableCell align="right">
                        ${Number(card.balance).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={card.status === "blocked" ? "Blocked" : "Active"}
                          color={card.status === "blocked" ? "error" : "success"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={card.status !== "blocked"}
                          onChange={() => toggleBlock(card)}
                        />
                        <IconButton
                          color="info"
                          onClick={() => openBalanceModal(card)}
                        >
                          <AccountBalanceWallet />
                        </IconButton>
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <TablePagination
            component="div"
            count={cardsTotal}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
          />
        </Card>
      )}

      {/* Balance Modal */}
      <Dialog open={balanceModalOpen} onClose={() => setBalanceModalOpen(false)}>
        <DialogTitle>Adjust Balance</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            value={newBalance}
            onChange={(e) => setNewBalance(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBalanceModalOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={saveBalance} variant="contained" color="success">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}